import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3203';
import { luHandleAppiError } from '../U3203Service';
import {
  doGetProvider,
  doSearchProviders,
  type U3202AiModelChild,
  type U3202OrgProviderConfigSearchRow,
} from '../../u3202AiProvider/U3202Service';

export interface U3203OrgProviderOption {
  id: string;
  code: string;
  name: string;
}

export interface U3203OrgModelOption {
  id: string;
  code: string;
  name: string;
}

export function useU3203OrgProviderModelHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [providers, setProviders] = useState<U3203OrgProviderOption[]>([]);
  const [modelsByProvider, setModelsByProvider] = useState<Record<string, U3203OrgModelOption[]>>({});
  const [loadedProviderIds, setLoadedProviderIds] = useState<Record<string, true>>({});
  const [loading, setLoading] = useState(false);

  const loadProviders = useCallback(async (): Promise<U3203OrgProviderOption[]> => {
    setLoading(true);
    try {
      const response = await doSearchProviders(u2000AxiosAuthenticated, {
        filter: {
          criterias: [],
          fields: [{ name: 'id' }, { name: 'code' }, { name: 'name' }, { name: 'active' }],
          sortedFields: [{ name: 'name', sortOrder: 'ASC' }],
        },
        pagination: { page: 1, size: 500 },
      });
      const rows = (response.data?.results ?? []) as U3202OrgProviderConfigSearchRow[];
      const active = rows
        .filter((row) => row.active)
        .map((row) => ({ id: row.id, code: row.code, name: row.name }));
      setProviders(active);
      return active;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setProviders([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [u2000AxiosAuthenticated, t, toast]);

  const loadModelsForProvider = useCallback(
    async (providerId: string | null): Promise<U3203OrgModelOption[]> => {
      if (!providerId) return [];
      if (modelsByProvider[providerId]) {
        return modelsByProvider[providerId];
      }
      setLoading(true);
      try {
        const response = await doGetProvider(u2000AxiosAuthenticated, providerId);
        const provider = response.data?.result;
        const models = (provider?.models ?? [])
          .filter((m: U3202AiModelChild) => m.active)
          .map((m: U3202AiModelChild) => ({ id: m.id, code: m.code, name: m.name }));
        setModelsByProvider((prev) => ({ ...prev, [providerId]: models }));
        setLoadedProviderIds((prev) => ({ ...prev, [providerId]: true }));
        return models;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        setLoadedProviderIds((prev) => ({ ...prev, [providerId]: true }));
        return [];
      } finally {
        setLoading(false);
      }
    },
    [modelsByProvider, u2000AxiosAuthenticated, t, toast],
  );

  const getModelsForProvider = useCallback(
    (providerId: string | null): U3203OrgModelOption[] => {
      if (!providerId) return [];
      return modelsByProvider[providerId] ?? [];
    },
    [modelsByProvider],
  );

  const hasModelsLoadedForProvider = useCallback(
    (providerId: string | null): boolean => {
      if (!providerId) return false;
      return loadedProviderIds[providerId] === true;
    },
    [loadedProviderIds],
  );

  return { providers, loading, loadProviders, loadModelsForProvider, getModelsForProvider, hasModelsLoadedForProvider };
}
